import { usersApi } from "#/api/usersApi";
import Pagination from "#/components/buttons/Pagination";
import BaseTable, { type TableSortProps } from "#/components/tables/BaseTable";
import { ClientUserPermission, type User, UserStatus } from "#/types/user";
import { sanitize } from "#/utils/StringUtils";
import { logger } from "#/utils/logger";
import { Select, Switch, Table } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import UserStatusModal from "./UserStatusModal";
import { usePermission } from "#/hooks/usePermission";

export interface Props {
  clientId: number;
}

const UsersTable = ({ clientId }: Props) => {
  const { t } = useTranslation();
  const { canWrite } = usePermission();
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(25);
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");
  const headers = [
    { key: "active", value: t("clients.users.columns.active") },
    { key: "id", value: t("clients.users.columns.id") },
    { key: "name", value: t("clients.users.columns.name") },
    { key: "email", value: t("clients.users.columns.email") },
    { key: "permission", value: t("clients.users.columns.permission") },
    { key: "status", value: t("clients.users.columns.status") },
  ];
  const [sortBy, setSortBy] = useState<string>("createdAt");

  const [statusModalOpened, { open: openStatusModal, close: closeStatusModal }] =
    useDisclosure(false);
  const [userToToggle, setUserToToggle] = useState<User>();

  const {
    data: { items = [], total = 0, per_page = 0 } = {},
    isPending,
    refetch,
  } = useQuery({
    queryKey: ["users", clientId, page, limit, sortDirection, sortBy],

    queryFn: async () => {
      try {
        const response = await usersApi.getAll({
          clientId: clientId.toString(),
          page,
          limit,
        });

        return {
          items: response.data.data,
          total: response.data.total,
          per_page: response.data.limit,
        };
      } catch {
        throw new Error("No users returned");
      }
    },
    enabled: !!clientId,
    staleTime: 1000 * 60,
    retry: false,
  });

  const updatePermissionMutation = useMutation({
    mutationFn: async ({ userId, permission }: { userId: string; permission: ClientUserPermission }) => {
      return await usersApi.update(userId, { clientPermission: permission });
    },
    onSuccess: () => {
      void refetch();
    },
    onError: (error) => {
      logger.error("UsersTable updatePermission error:", error);
    },
  });

  return (
    <>
      <div className="w-full h-full flex flex-col">
        <div className="flex-1 overflow-auto min-h-0">
          <BaseTable
            headers={headers.map((item) => item.value)}
            loading={isPending}
            allowSorting={false}
            sortableColumns={[2, 3, 4]}
            onSortChange={(e: TableSortProps) => {
              setPage(1);
              setSortDirection(e.direction);
              setSortBy(headers[e.index].key);
            }}
            defaultSort={{
              direction: "DESC",
              index: 5,
            }}
            rows={items?.map((item) => {
              const isActive = item.status === UserStatus.ACTIVE;
              return (
                <Table.Tr key={item.id}>
                  <Table.Td className="w-16">
                    <Switch
                      checked={isActive}
                      aria-label={
                        isActive
                          ? t("clients.users.deactivateUser")
                          : t("clients.users.activateUser")
                      }
                      onChange={() => {
                        setUserToToggle(item);
                        openStatusModal();
                      }}
                    />
                  </Table.Td>
                  <Table.Td className="w-10 max-w-40">
                    {item.id ?? "--"}
                  </Table.Td>
                  <Table.Td className="capitalize">
                    {item.name ?? "--"}
                  </Table.Td>
                  <Table.Td>{item?.email ?? "--"}</Table.Td>
                  <Table.Td style={{ width: '130px' }}>
                    <Select
                      value={item?.clientPermission || ClientUserPermission.FULL}
                      onChange={(value) => {
                        if (value) {
                          updatePermissionMutation.mutate({
                            userId: item.id,
                            permission: value as ClientUserPermission
                          });
                        }
                      }}
                      data={Object.values(ClientUserPermission).map((permission) => ({
                        label: sanitize(permission),
                        value: permission,
                      }))}
                      size="xs"
                      disabled={!isActive || !canWrite()}
                      comboboxProps={{ width: 130 }}
                    />
                  </Table.Td>
                  <Table.Td
                    className={`uppercase text-sm font-bold ${isActive ? "text-teal-500" : "text-red-500"}`}
                  >
                    {item?.status ?? "--"}
                  </Table.Td>
                </Table.Tr>
              );
            })}
          />
        </div>
        {total > 0 && (
          <div className="w-full bg-white border-t border-gray-200 py-2 flex-shrink-0">
            <Pagination
              page={page}
              total={total}
              limit={per_page}
              onChange={setPage}
              onLimitChange={(newLimit) => {
                setLimit(newLimit);
                setPage(1);
              }}
            />
          </div>
        )}
      </div>

      <UserStatusModal
        opened={statusModalOpened}
        onClose={closeStatusModal}
        user={userToToggle}
        onSuccess={() => {
          void refetch();
          closeStatusModal();
        }}
      />
    </>
  );
};

export default UsersTable;
